import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos } from '@/mocks/videos';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <>
        <Navbar />
        <ScrollToTop />
        <main className="w-full px-4 md:px-6 py-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的视频可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id)
    .slice(0, 3);
  const otherVideos = videos
    .filter((v) => v.id !== video.id && v.categorySlug !== video.categorySlug)
    .slice(0, 3);

  const formatViews = (n: number): string => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: video.category, to: `/category/${video.categorySlug}` },
    { label: video.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: video.publishDate,
    author: {
      '@type': 'Person',
      name: video.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '吃瓜视频',
      url: 'https://readyue.cn',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://readyue.cn/article/${video.id}`,
    },
    image: video.thumbnail,
  };

  return (
    <>
      <Navbar />
      <ScrollToTop />
      <main>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

        <div className="w-full px-4 md:px-6 pt-6 pb-4">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-4 md:py-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-3">
                {video.category}
              </span>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-900 leading-snug">
                {video.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {video.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {video.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(video.views)} 次观看
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </span>
              </div>
            </div>

            <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-6 bg-background-200">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-600/90 hover:scale-110 transition-all duration-300">
                  <i className="ri-play-fill text-2xl md:text-3xl ml-1"></i>
                </div>
              </div>
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-4 mb-8">
              {video.content.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8 pt-6 border-t border-background-200/70">
              <span className="text-xs text-foreground-500 mr-1">标签：</span>
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-600 cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-4 border-t border-background-200/70 mb-10">
              <Link
                to={`/category/${video.categorySlug}`}
                className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回{video.category}分类
              </Link>
              <div className="flex items-center gap-3">
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
                  <i className="ri-bookmark-line"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                <h2 className="text-base md:text-lg font-semibold text-foreground-900">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}

        {otherVideos.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-1 h-5 rounded-full bg-accent-500"></div>
                <h2 className="text-base md:text-lg font-semibold text-foreground-900">猜你喜欢</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {otherVideos.map((v) => (
                  <VideoCard key={v.id} video={v} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}